export interface ProfilePoint {
  d: number // cumulative distance in km
  e: number // elevation in m
}

function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371
  const dLat = ((b[1] - a[1]) * Math.PI) / 180
  const dLng = ((b[0] - a[0]) * Math.PI) / 180
  const lat1 = (a[1] * Math.PI) / 180
  const lat2 = (b[1] * Math.PI) / 180
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

// Build an elevation-vs-distance series from a track. Accepts LineString or
// MultiLineString ([lng, lat, ele] points). Distance accumulates within each
// segment and carries across segments (inter-segment gaps are not added, so the
// x-axis ~ moving distance). Downsamples to maxPoints for a fast, clean chart.
export function buildElevationProfile(track: any, maxPoints = 200): ProfilePoint[] {
  if (!track || !track.coordinates) return []
  const segments = track.type === 'MultiLineString' ? track.coordinates : [track.coordinates]

  const pts: ProfilePoint[] = []
  let cum = 0
  for (const seg of segments) {
    if (!Array.isArray(seg)) continue
    let prevGeo: number[] | null = null
    for (const c of seg) {
      if (!Array.isArray(c) || c.length < 2) continue
      if (prevGeo) cum += haversineKm([prevGeo[0], prevGeo[1]], [c[0], c[1]])
      prevGeo = c
      const ele = c[2]
      if (typeof ele === 'number' && Number.isFinite(ele)) {
        pts.push({ d: cum, e: ele })
      }
    }
  }

  if (pts.length <= maxPoints) return pts

  // Stride downsample, always keeping the last point.
  const stride = Math.ceil(pts.length / maxPoints)
  const out: ProfilePoint[] = []
  for (let i = 0; i < pts.length; i += stride) out.push(pts[i])
  if (out[out.length - 1] !== pts[pts.length - 1]) out.push(pts[pts.length - 1])
  return out
}
